import {useUpmPlant} from '@hconnect/upm'
import {Modal, ModalTitle, ModalContent, Button, Loading, TabsPanel, Tab} from '@hm/fundamental'
import {mdiClose} from '@mdi/js'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {useUpdateInstallationForm} from './hooks'
import {BrickCalculation} from './Tabs/BrickCalculation'
import {GeneralDetailsTab} from './Tabs/GeneralDetailsTab'

import {useKilns} from '@refra/modules/common/providers'
import type {Refractory} from '@refra/modules/refra-catalog/types'
import {useDeleteMaterialInstallation} from '@refra/modules/refra-planning/hooks/mutations'
import type {MaterialInstallationDetails} from '@refra/modules/refra-planning/types'
import {useUrlParam} from '@refra/modules/routing/hooks'

interface UpdateInstallationModalProps {
  min: number
  max: number
  shutdownId: number
  interventionDate: string
  materialInstallationDetails: MaterialInstallationDetails
  assignedRefractory: Refractory
  isReadOnly?: boolean
}

enum InstallationTab {
  General = 'general',
  BrickCalculation = 'brickCalculation'
}

export const UpdateInstallationModalTabs: React.FC<UpdateInstallationModalProps> = ({
  min,
  max,
  materialInstallationDetails,
  shutdownId,
  interventionDate,
  assignedRefractory,
  isReadOnly = false
}) => {
  const {t} = useTranslation()

  const plantId = useUrlParam('plantCode')
  const upmPlant = useUpmPlant({plantId})
  const [selectedTab, setSelectedTab] = useState<InstallationTab>(InstallationTab.General)

  const {selectedKiln} = useKilns()

  const {mutate: deleteInstallation, isPending: isDeletingInstallation} =
    useDeleteMaterialInstallation()

  const {formMethods, isFormBeingProcessed, onClose, onSubmit} = useUpdateInstallationForm({
    materialInstallation: materialInstallationDetails,
    assignedRefractory,
    shutdownId
  })

  const isModalDisabled = isReadOnly || isFormBeingProcessed || isDeletingInstallation

  const selectModalContent = () => {
    if (!materialInstallationDetails) return <Loading size="large" />
    switch (selectedTab) {
      case InstallationTab.General:
        return (
          <GeneralDetailsTab
            shutdownId={shutdownId}
            interventionDate={interventionDate}
            materialInstallationDetails={materialInstallationDetails}
            formMethods={formMethods}
            min={min}
            max={max}
            assignedRefractory={assignedRefractory}
            isModalDisabled={isModalDisabled}
          />
        )
      case InstallationTab.BrickCalculation:
        return (
          <BrickCalculation
            shutdownId={shutdownId}
            materialInstallation={materialInstallationDetails}
            formMethods={formMethods}
            isReadOnly={isReadOnly}
          />
        )
      default:
        return null
    }
  }

  return (
    <Modal
      open
      onClose={() => onClose()}
      data-cy="refra_update_material_modal"
      className="min-w-[950px]"
    >
      <div className={'flex justify-between'}>
        <ModalTitle data-cy="refra_update_material_modal_title">
          {t('refraPlanning.materialInstallations.materialDetail')}
        </ModalTitle>
        <Button
          icon={mdiClose}
          variant="tertiary"
          onClick={onClose}
          data-cy="refra_update_material_modal_close_btn"
        />
      </div>
      <ModalContent>
        <TabsPanel onSelect={(selectedTab) => setSelectedTab(selectedTab as InstallationTab)}>
          <Tab label={t('common.general')} value="general" />
          <Tab label={t('refraPlanning.brickCalculation.title')} value="brickCalculation" />
        </TabsPanel>
        <div className="flex flex-col gap-4">
          {selectModalContent()}
          <div className={'flex justify-between'}>
            <Button
              type="button"
              variant="tertiary"
              disabled={isModalDisabled}
              onClick={() =>
                deleteInstallation({
                  plantUpmId: upmPlant.upmId,
                  kilnUpmId: selectedKiln.upmId,
                  shutdownId,
                  installationId: materialInstallationDetails.id
                })
              }
              className="text-red-500"
              data-cy="delete_material_btn"
            >
              {t('refraPlanning.materialInstallations.removeMaterial')}
            </Button>
            <Button
              type="button"
              disabled={isModalDisabled}
              onClick={onSubmit}
              data-cy="update_material_btn"
            >
              {t('common.done')}
            </Button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}
